import styles from "exports.module.scss";

import type { ModuleType } from "seneca-common/features/module/module-types/types";
import { ModuleRegistry } from "seneca-common/features/module/registry";
import { getDimensionFromCSSVar } from "seneca-common/utils/functions";

import { isMobile } from "features/common/utils/functions/mobile";

const moduleRegistry = ModuleRegistry.getInstance();

export function filterForDevice(moduleType: ModuleType): boolean {
  if (!moduleRegistry.modules.hasModule(moduleType)) return false;

  const moduleObject = moduleRegistry.modules.getModule(moduleType);

  const { desktopOnly, minModuleWidth } = moduleObject.getDeviceRequirements();

  const availableForDevice = !(desktopOnly && isMobile());

  // If a user is on a mobile device they could rotate their screen mid session
  // and change the way this filtering works. This potentially causes issues in
  // wrong answer mode, where we calculate the number of questions are available
  // on the device and only allow the user to start studying if > 0 questions.
  // If the user starts studying then rotates the device, that could potentially
  // drop the number of questions to 0.
  //
  // On mobile we therefore filter the modules based on the smallest screen dimension
  // (rather than which happens to be width right now). On Desktop we do not have the
  // same problem; because users can't rotate their screen in the same way.
  const minimumScreenDimension = isMobile()
    ? Math.min(window.innerWidth, window.innerHeight)
    : window.innerWidth;

  const availableForScreenWidth = moduleTypeFitsUserScreenWidth(
    minModuleWidth,
    minimumScreenDimension
  );

  return availableForDevice && availableForScreenWidth;
}

const SESSION_MOBILE_BREAK_POINT = getDimensionFromCSSVar(
  styles.sessionmobilebreakpoint
);
const EDGE_OF_SESSION_PADDING = getDimensionFromCSSVar(
  styles.edgeofsessionpadding
);
const EDGE_OF_SESSION_PADDING_MOBILE = getDimensionFromCSSVar(
  styles.edgeofsessionpaddingmobile
);

function moduleTypeFitsUserScreenWidth(
  minModuleWidth: number,
  userScreenWidth: number
): boolean {
  const edgeOfSessionPadding =
    userScreenWidth > SESSION_MOBILE_BREAK_POINT
      ? EDGE_OF_SESSION_PADDING
      : EDGE_OF_SESSION_PADDING_MOBILE;
  return userScreenWidth >= minModuleWidth + 2 * edgeOfSessionPadding;
}
